import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Boxes, Fuel, ArrowRightLeft, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/MickChat";

function timeAgo(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export function BlockExplorer() {
  const [blocks, setBlocks] = useState([]);
  const [network, setNetwork] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/blockchain/blocks");
        if (alive) {
          setBlocks(data.blocks || []);
          setNetwork(data.network);
        }
      } catch {}
    };
    load();
    const t = setInterval(load, 6000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const openBlock = async (height) => {
    setSelected(height);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/blockchain/block/${height}`);
      setDetail(data);
    } catch {
      setDetail({ error: true });
    } finally {
      setLoadingDetail(false);
    }
  };

  const doSearch = () => {
    const n = parseInt(search.trim(), 10);
    if (!isNaN(n)) openBlock(n);
  };

  return (
    <section id="explorer" className="relative py-20 px-4 lg:px-8 cyber-grid">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <SectionHeader
            eyebrow="Live Ledger"
            title="AOX Blockchain Hash-Block Stream"
            desc={`Real blocks streaming from ${network || "the AOX ledger"} — genuine hashes, refreshing every few seconds.`}
          />
          <div className="flex items-center gap-2 bg-aox-card border border-aox-cyan/25 rounded-md pl-3 pr-1 py-1">
            <Search size={16} className="text-aox-cyan/60" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Block height…"
              className="bg-transparent text-sm text-white outline-none w-32 font-mono-aox"
              data-testid="explorer-search-input"
            />
            <button
              onClick={doSearch}
              className="bg-aox-cyan/15 text-aox-cyan text-xs font-bold px-3 py-1.5 rounded"
              data-testid="explorer-search-btn"
            >
              Inspect
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="explorer-block-grid">
          <AnimatePresence mode="popLayout">
            {blocks.map((b, i) => (
              <motion.button
                key={b.height}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
                onClick={() => openBlock(b.height)}
                className={`text-left bg-aox-card border clip-chamfer p-4 hover:-translate-y-1 transition-all ${
                  i === 0 ? "border-aox-green/50 glow-green" : "border-aox-cyan/20 hover:border-aox-cyan/50"
                }`}
                data-testid={i === 0 ? "explorer-block-latest" : `explorer-block-${b.height}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Boxes size={15} className={i === 0 ? "text-aox-green" : "text-aox-cyan"} />
                    <span className="font-mono-aox text-sm font-bold text-white">
                      #{b.height}
                    </span>
                  </div>
                  {i === 0 && (
                    <span className="w-2 h-2 rounded-full bg-aox-green pulse-dot" />
                  )}
                </div>
                <div className="font-mono-aox text-[10px] text-slate-400 truncate mb-3">
                  {b.hash}
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono-aox text-slate-500">
                  <span>{b.tx_count} txns</span>
                  <span>{timeAgo(b.timestamp)}</span>
                </div>
                <div className="mt-2 h-1.5 bg-aox-bg rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-aox-cyan to-aox-green"
                    style={{ width: `${b.gas_pct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1.5 text-[9px] font-mono-aox text-slate-600 uppercase tracking-widest">
                  <span>Gas {b.gas_pct}%</span>
                  <span className="text-aox-green">+{b.aox_reward} AOX</span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
          {blocks.length === 0 && (
            <div className="col-span-full flex items-center justify-center py-16 text-aox-cyan/50">
              <Loader2 className="animate-spin mr-2" /> Syncing with AOX ledger…
            </div>
          )}
        </div>
      </div>

      {/* detail modal */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="explorer-modal"
          >
            <motion.div
              className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-aox-card border border-aox-cyan/30 clip-chamfer p-6 glow-cyan"
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute right-4 top-4 text-aox-cyan/60 hover:text-aox-cyan"
                data-testid="explorer-modal-close"
              >
                <X size={20} />
              </button>
              <div className="font-mono-aox text-xs uppercase tracking-widest text-aox-green mb-1">
                AOX Block Inspector
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-5">
                Block #{selected}
              </h3>

              {loadingDetail && (
                <div className="flex items-center gap-2 text-aox-cyan/60 py-8 justify-center">
                  <Loader2 className="animate-spin" /> Fetching from node…
                </div>
              )}
              {detail?.error && (
                <p className="text-red-400 py-6">Unable to reach the AOX ledger node for this block.</p>
              )}
              {detail && !detail.error && (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <Field label="Hash" value={detail.hash} mono full />
                    <Field label="Timestamp" value={new Date(detail.timestamp * 1000).toLocaleString()} />
                    <Field label="Transactions" value={detail.tx_count} icon={ArrowRightLeft} />
                    <Field label="Gas Used" value={`${detail.gas_pct}%`} icon={Fuel} />
                    <Field label="Validator" value={detail.validator} mono />
                    <Field label="AOX Reward" value={`+${detail.aox_reward}`} green />
                  </div>
                  {detail.transactions?.length > 0 && (
                    <div>
                      <div className="font-mono-aox text-xs uppercase tracking-widest text-aox-cyan mb-2">
                        Transaction Tree ({detail.transactions.length} shown)
                      </div>
                      <div className="space-y-1.5">
                        {detail.transactions.map((t) => (
                          <div
                            key={t.hash}
                            className="bg-aox-bg border border-aox-cyan/15 rounded p-2.5 font-mono-aox text-[10px]"
                          >
                            <div className="text-aox-cyan/80 truncate">{t.hash}</div>
                            <div className="flex justify-between text-slate-500 mt-1">
                              <span className="truncate max-w-[45%]">{t.from?.slice(0, 12)}…</span>
                              <span className="text-aox-green">{t.value_eth} Ξ</span>
                              <span className="truncate max-w-[45%]">→ {t.to?.slice(0, 12)}…</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Field({ label, value, mono, green, full, icon: Icon }) {
  return (
    <div className={`bg-aox-bg border border-aox-cyan/15 rounded-md p-3 ${full ? "col-span-2" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div
        className={`text-sm break-all ${mono ? "font-mono-aox" : ""} ${
          green ? "text-aox-green font-bold" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
